import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import i18next from "i18next";

import {
  DepartmentManager,
  GATAdmin,
  HRManager,
  LimitedAccessSupervisor,
  ReadOnly,
  ShiftSupervisor,
} from "../../config/permissions";
import axios from "../../utils/api-client";
import { applySupervisorLanguageOnce } from "../../utils/language";
import { RoleContextType } from "../../utils/types";

const readPermissionsFromStorage = (): string[] => {
  try {
    const raw = localStorage.getItem("permissions");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

function isInternelEmail(email: string): boolean {
  try {
    if (typeof email !== "string" || email.trim() === "") {
      throw new Error("Invalid email input");
    }

    const lowercaseEmail = email.toLowerCase();
    return (
      lowercaseEmail.endsWith("@gigandtake.com") ||
      lowercaseEmail.endsWith("@wilya.com")
    );
  } catch (error) {
    console.error("Error in isInternalEmail:", error);
    return false;
  }
}

interface UserData {
  username: string;
  userRoles: string[];
  userRole: string | null;
  id: string | null;
  firstName: string;
  lastName: string;
  defaultDurationHrs: number;
  minDurationHrs: number;
  maxDurationHrs: number;
  showCrossSkilling: boolean;
  shiftCancelReasonEnabled: boolean;
  skillPriorityEnabled: boolean;
  useObsoleteSkills: boolean;
  customizedBrandingEnabled: boolean;
  skillExpiryEnabled: boolean;
  certificationsEnabled: boolean;
  skillInsightsEnabled: boolean;
  jobsMatrixEnabled: boolean;
  supervisorDashboardEnabled: boolean;
  defaultUserView: string;
  showHeatMap: boolean;
  showWorkerProfile: boolean;
  showJobExpiry: boolean;
  showShiftPurpose: boolean;
  fullTimeSchedule: boolean;
  showShiftSwap: boolean;
  restrictFlexHours: boolean;
  isSchedulerOffset: boolean;
  company: string;
  companyId: number | null;
  attendanceManagement: boolean;
  leaveCalendarHeatmap: boolean;
  callOffLineEnabledLocations: number[];
  attendanceManagementEnabledLocations: number[];
  defaultShiftSkillLevel: number;
  shiftBySkills: boolean;
  assignShiftToWorkerEnabled: boolean;
  userRolesWithPermissions?: RoleContextType[];
  weekStartDay: string;
  weekStartTime: string;
  jobSkillLevelMapping: boolean;
  workerSubTypeEnabled: boolean;
  skillLevelRequirementsEnabled: boolean;
  allowPartialSignups: boolean;
  badgeNumberEnabled: boolean;
  badgeNumberEnabledLocations: number[];
  ftSchedulerEnabled: boolean;
  ftSchedulerEnabledLocations: number[];
  FTScheduleGenerationEnabledLocations: number[];
  maxBroadcastMessageLength: number;
  skillsMatrixExportEnabled: boolean;
  EnableSkillEvaluationForm: boolean;
  leaveBalanceUpdateEnabled: boolean;
  vakaySSOLinkEnabled: boolean;
  punchImportEnabled: boolean;
  shiftWorkerTypeFilterEnabled: boolean;
  urgentShiftEnabledLocations: number[];
  minWeekHoursNotifEnabledLocations: number[];
  supervisorMultilingualEnabled: boolean;
  languagePreference: string | null;
  permissions: string[];
}

export interface UserState {
  userData: UserData;
  authenticated: boolean;
  assertVakaySSO: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
    redirectUrl: string;
  };
  login: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
    maxFailedAttempts?: number;
    failedAttempt?: number;
    isLocked?: boolean;
  };
  verifySSO: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
    isSSOEnabled?: boolean;
    ssoLoginUrl?: string;
  };
  authenticateSSO: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
  };
  forgotPassword: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
  };
  resetPassword: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
  };
  customLogo: any[]; // Define the appropriate type for customLogo
  getCustomLogo: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
  };
  supervisorRateLimitError: {
    isErrorOccurred: boolean;
  };
  validateResetToken: {
    status: "idle" | "pending" | "fulfilled" | "error";
    errorMessage: string;
    isValid?: boolean;
  };
}

export const loginUser = createAsyncThunk(
  "user/login",
  async (
    { username, password }: { username: string; password: string },
    thunkAPI
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/Users/authenticate`,
        {
          username,
          password,
          clientType: "web",
        }
      );
      if (response.status === 200) {
        const data = response.data;
        localStorage.setItem("token", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem(
          "permissions",
          JSON.stringify(data.permissions ?? [])
        );
        // Seed the supervisor UI language from the login response (flag off →
        // English). The reducer sets the gating flag; this applies the language.
        applySupervisorLanguageOnce(
          data.settings?.supervisorMultilingualEnabled === true,
          data.languagePreference
        );
        return thunkAPI.fulfillWithValue(data);
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("common_error-occurred"),
          maxFailedAttempts: 0,
          failedAttempt: 0,
          isLocked: false,
        });
      }
    } catch (e: any) {
      const defaultData = {
        message: i18next.t("common_error-occurred"),
        maxFailedAttempts: 0,
        failedAttempt: 0,
        isLocked: false,
        status: e?.response?.status,
      };
      const response = Object.prototype.hasOwnProperty.call(e, "response")
        ? e.response
        : { data: defaultData };
      const data = Object.prototype.hasOwnProperty.call(response, "data")
        ? response.data
        : defaultData;
      if (Object.prototype.hasOwnProperty.call(data, "message")) {
        defaultData.message = data.message;
      }
      if (
        Object.prototype.hasOwnProperty.call(data, "maxFailedAttempts") &&
        Object.prototype.hasOwnProperty.call(data, "failedAttempt") &&
        Object.prototype.hasOwnProperty.call(data, "isLocked")
      ) {
        return thunkAPI.rejectWithValue({
          message: e.response.data.message,
          status: e?.response?.status,
          maxFailedAttempts: e.response.data.maxFailedAttempts,
          failedAttempt: e.response.data.failedAttempt,
          isLocked: e.response.data.isLocked,
        });
      } else {
        return thunkAPI.rejectWithValue(defaultData);
      }
    }
  }
);

export const verifySSO = createAsyncThunk(
  "user/verifySSO",
  async ({ username }: { username: string }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/Users/verify`,
        {
          username,
          appUrl: process.env.REACT_APP_URL,
        }
      );
      if (response.status === 200) {
        const data = response.data;
        return thunkAPI.fulfillWithValue(data);
      } else {
        return thunkAPI.rejectWithValue(i18next.t("common_error-occurred"));
      }
    } catch (e: any) {
      const defaultData = {
        message: i18next.t("common_error-occurred"),
        maxFailedAttempts: 0,
        failedAttempt: 0,
        isLocked: false,
        status: e?.response?.status,
      };
      const response = Object.prototype.hasOwnProperty.call(e, "response")
        ? e.response
        : { data: defaultData };
      const data = Object.prototype.hasOwnProperty.call(response, "data")
        ? response.data
        : defaultData;
      if (Object.prototype.hasOwnProperty.call(data, "message")) {
        defaultData.message = data.message;
      }
      if (
        Object.prototype.hasOwnProperty.call(data, "maxFailedAttempts") &&
        Object.prototype.hasOwnProperty.call(data, "failedAttempt") &&
        Object.prototype.hasOwnProperty.call(data, "isLocked")
      ) {
        return thunkAPI.rejectWithValue({
          message: e.response.data.message,
          maxFailedAttempts: e.response.data.maxFailedAttempts,
          failedAttempt: e.response.data.failedAttempt,
          isLocked: e.response.data.isLocked,
          status: e?.response?.status,
        });
      } else {
        return thunkAPI.rejectWithValue(defaultData);
      }
    }
  }
);

export const authenticateSSO = createAsyncThunk(
  "user/authenticateSSO",
  async (data: any, thunkAPI) => {
    try {
      const params = { ...data };
      params.appUrl = process.env.REACT_APP_URL;
      params.clientType = "web";
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/Users/authenticateSSO`,
        params
      );
      if (response.status === 200) {
        const data = response.data;
        localStorage.setItem("token", data.token);
        localStorage.setItem("refreshToken", data.refreshToken);
        localStorage.setItem(
          "permissions",
          JSON.stringify(data.permissions ?? [])
        );
        // Seed the supervisor UI language from the SSO login response.
        applySupervisorLanguageOnce(
          data.settings?.supervisorMultilingualEnabled === true,
          data.languagePreference
        );
        return thunkAPI.fulfillWithValue(data);
      } else {
        return thunkAPI.rejectWithValue(i18next.t("common_error-occurred"));
      }
    } catch (e: any) {
      const errorResponse = e?.response?.data;
      errorResponse.status = e?.response?.status;
      return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

export const assertVakaySSO = createAsyncThunk(
  "user/assertVakaySSO",
  async (_, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/internalsso/assert`,
        {}
      );
      if (response.status === 200) {
        return thunkAPI.fulfillWithValue(response.data);
      } else {
        return thunkAPI.rejectWithValue(i18next.t("common_error-occurred"));
      }
    } catch (e: any) {
      const errorResponse = e?.response?.data;
      errorResponse.status = e?.response?.status;
      return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "user/forgotPassword",
  async (payload: { email: string }, { fulfillWithValue, rejectWithValue }) => {
    let { email } = payload;
    email = email.toString();
    try {
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API_END_POINT}/Login/Reset`,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        data: JSON.stringify(email),
      });

      //  console.log(response);

      if (response.status === 204) {
        //console.log(response);
        return fulfillWithValue("fulfilled");
      } else return rejectWithValue(response.data);
    } catch (error: unknown) {
      // Type casting the error to AxiosError type
      const axiosError = error as AxiosError;

      if (axiosError.response && axiosError.response.status === 429) {
        // Handle 429 error by passing the message from the response
        return rejectWithValue({
          message:
            (axiosError.response.data as { message?: string })?.message ||
            i18next.t("user_too-many-requests"),
          status: axiosError?.response?.status,
        });
      } else {
        // Handle any other errors without passing a specific message
        return rejectWithValue(i18next.t("user_email-send-error"));
      }
    }
  }
);

export const resetPassword = createAsyncThunk(
  "user/resetPassword",
  async (
    payload: {
      d: string | null;
      token: string | null;
      newPassword: string;
      confirmPassword: string;
    },
    { fulfillWithValue, rejectWithValue }
  ) => {
    const { d, token, newPassword } = payload;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/Login/Verify`,
        {
          d,
          token,
          password: newPassword,
        }
      );

      if (response.status === 204) {
        return fulfillWithValue("successful");
      } else return rejectWithValue(response.data);
    } catch (error: any) {
      const errorResponse = error?.response?.data ?? "Error";
      errorResponse.status = error?.response?.status;
      console.error("Error", error);
      return rejectWithValue(errorResponse);
    }
  }
);
export const validateResetToken = createAsyncThunk(
  "user/validateResetToken",
  async (
    payload: {
      d: string | null;
      token: string | null;
    },
    { fulfillWithValue, rejectWithValue }
  ) => {
    const { d, token } = payload;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_END_POINT}/Login/ValidateResetToken`,
        {
          d,
          token
        }
      );
      if (response && response.status === 200) {
        return fulfillWithValue(response.data);
      } else return rejectWithValue(response.data);
    } catch (error: any) {
      const errorResponse = error?.response?.data ?? "Error";
      errorResponse.status = error?.response?.status;
      console.error("Error", error);
      return rejectWithValue(errorResponse);
    }
  }
);

export const getCustomLogo = createAsyncThunk(
  "user/getCustomLogo",
  async (payload, { fulfillWithValue, rejectWithValue, getState }) => {
    try {
      const companyId = (getState() as any).user?.userData.companyId;
      const response = await axios.get(
        `${process.env.REACT_APP_API_END_POINT}/Companies/Branding?CompanyId=${companyId}`
      );
      if (response.status === 200) {
        return fulfillWithValue(response.data);
      } else return rejectWithValue(response.data);
    } catch (error: any) {
      const errorResponse = error?.response?.data ?? "Error";
      errorResponse.status = error?.response?.status;
      return rejectWithValue(errorResponse);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    userData: {
      username: "",
      userRoles: [],
      userRole: null,
      id: null,
      firstName: "",
      lastName: "",
      defaultDurationHrs: 2,
      minDurationHrs: 1,
      maxDurationHrs: 8,
      showCrossSkilling: false,
      shiftCancelReasonEnabled: false,
      skillPriorityEnabled: false,
      useObsoleteSkills: false,
      customizedBrandingEnabled: false,
      skillExpiryEnabled: false,
      certificationsEnabled: false,
      skillInsightsEnabled: false,
      jobsMatrixEnabled: false,
      supervisorDashboardEnabled: false,
      defaultUserView: "FlexScheduler",
      showHeatMap: false,
      showWorkerProfile: false,
      showJobExpiry: false,
      showShiftPurpose: false,
      fullTimeSchedule: false,
      showShiftSwap: false,
      restrictFlexHours: false,
      isSchedulerOffset: false,
      company: "",
      companyId: null,
      attendanceManagement: false,
      leaveCalendarHeatmap: false,
      callOffLineEnabledLocations: [],
      attendanceManagementEnabledLocations: [],
      defaultShiftSkillLevel: 0,
      shiftBySkills: false,
      assignShiftToWorkerEnabled: false,
      weekStartDay: "Monday",
      weekStartTime: "00:00:00",
      jobSkillLevelMapping: false,
      workerSubTypeEnabled: false,
      skillLevelRequirementsEnabled: false,
      allowPartialSignups: false,
      badgeNumberEnabled: false,
      badgeNumberEnabledLocations: [],
      ftSchedulerEnabled: false,
      ftSchedulerEnabledLocations: [],
      FTScheduleGenerationEnabledLocations: [],
      maxBroadcastMessageLength: 160,
      skillsMatrixExportEnabled: false,
      EnableSkillEvaluationForm: false,
      leaveBalanceUpdateEnabled: false,
      vakaySSOLinkEnabled: false,
      punchImportEnabled: false,
      shiftWorkerTypeFilterEnabled: false,
      urgentShiftEnabledLocations: [],
      minWeekHoursNotifEnabledLocations: [],
      supervisorMultilingualEnabled: false,
      languagePreference: null,
      permissions: readPermissionsFromStorage(),
    },
    authenticated: false,
    assertVakaySSO: {
      status: "idle",
      errorMessage: "",
      redirectUrl: "",
    },
    login: {
      status: "idle",
      errorMessage: "",
    },
    verifySSO: {
      status: "idle",
      errorMessage: "",
      isSSOEnabled: undefined,
      ssoLoginUrl: undefined,
    },
    authenticateSSO: {
      status: "idle",
      errorMessage: "",
    },
    forgotPassword: {
      status: "idle",
      errorMessage: "",
    },
    resetPassword: {
      status: "idle",
      errorMessage: "",
    },
    customLogo: [],
    getCustomLogo: {
      status: "idle",
      errorMessage: "",
    },
    supervisorRateLimitError: {
      isErrorOccurred: false,
    },
    validateResetToken: {
      status: "idle",
      errorMessage: "",
      isValid: undefined,
    }
  } as UserState, // Initial state with correct types
  reducers: {
    setUserId: (state, action: PayloadAction<number>) => {
      state.userData.id = action.payload.toString();
    },
    setAuthenticated: (state, action) => {
      state.authenticated = action.payload;
    },
    setUser: (state, action) => {
      state.userData.id = action.payload.id;
      state.userData.userRoles = action.payload.userRoles ?? [];
      state.userData.permissions =
        action.payload.permissions && action.payload.permissions.length > 0
          ? action.payload.permissions
          : readPermissionsFromStorage();
      state.userData.userRolesWithPermissions =
        state.userData.userRolesWithPermissions = action.payload.userRoles.map(
          (role: string) => {
            let returnValue: RoleContextType = {
              role: "ReadOnly",
              permissions: {
                ...ReadOnly,
              },
            };
            switch (role) {
              case "ShiftSupervisor":
                returnValue = {
                  role: role,
                  permissions: {
                    ...ShiftSupervisor,
                  },
                };
                break;
              case "DepartmentManager":
                returnValue = {
                  role: role,
                  permissions: {
                    ...DepartmentManager,
                  },
                };
                break;
              case "HR Manager":
                returnValue = {
                  role: role,
                  permissions: {
                    ...HRManager,
                    REPORTS: {
                      ...HRManager.REPORTS,
                      UPDATE_LEAVE_BALANCE: action.payload.leaveBalanceUpdateEnabled ?? false,
                    }
                  },
                };
                break;
              case "LimitedAccessSupervisor":
                returnValue = {
                  role: role,
                  permissions: {
                    ...LimitedAccessSupervisor,
                  },
                };
                break;
              case "GATAdmin":
                returnValue = {
                  role: role,
                  permissions: {
                    ...GATAdmin,
                    REPORTS: {
                      ...GATAdmin.REPORTS,
                      UPDATE_LEAVE_BALANCE: action.payload.leaveBalanceUpdateEnabled ?? false,
                    }
                  },
                };
                break;
            }
            return returnValue;
          }
        );
      state.userData.showHeatMap = action.payload.showHeatMap;
      state.userData.showJobExpiry = action.payload.showJobExpiry;
      state.userData.showWorkerProfile = action.payload.showWorkerProfile;
      state.userData.showCrossSkilling = action.payload.showCrossSkilling;
      state.userData.shiftCancelReasonEnabled =
        action.payload.shiftCancelReasonEnabled;
      state.userData.showShiftPurpose = action.payload.showShiftPurpose;

      state.userData.skillPriorityEnabled = action.payload.skillPriorityEnabled;
      state.userData.useObsoleteSkills = action.payload.useObsoleteSkills;

      state.userData.customizedBrandingEnabled =
        action.payload.customizedBrandingEnabled;
      state.userData.skillExpiryEnabled = action.payload.skillExpiryEnabled;
      state.userData.certificationsEnabled =
        action.payload.certificationsEnabled;
      state.userData.skillInsightsEnabled = action.payload.skillInsightsEnabled;
      state.userData.jobsMatrixEnabled = action.payload.jobsMatrixEnabled;
      // setUser also runs on every silent token refresh, so an absent claim
      // must keep the current value — coercing undefined to false here would
      // turn the Home landing page off mid-session.
      state.userData.supervisorDashboardEnabled =
        action.payload.supervisorDashboardEnabled === undefined
          ? state.userData.supervisorDashboardEnabled
          : action.payload.supervisorDashboardEnabled === true;
      state.userData.defaultUserView = action.payload.defaultUserView;
      state.userData.fullTimeSchedule = action.payload.fullTimeSchedule;
      state.userData.ftSchedulerEnabled = action.payload.ftSchedulerEnabled;
      state.userData.ftSchedulerEnabledLocations =
        action.payload.ftSchedulerEnabledLocations;
      state.userData.FTScheduleGenerationEnabledLocations =
        action.payload.FTScheduleGenerationEnabledLocations;
      state.userData.showShiftSwap = action.payload.showShiftSwap;
      state.userData.restrictFlexHours = action.payload.restrictFlexHours;
      state.userData.isSchedulerOffset = action.payload.schedulerOffsetEnabled;
      state.authenticated = true;
      state.userData.defaultDurationHrs = action.payload.defaultDurationHrs;
      state.userData.minDurationHrs = action.payload.minDurationHrs;
      state.userData.maxDurationHrs = action.payload.maxDurationHrs;
      state.userData.weekStartDay = action.payload.schedulerOffsetEnabled
        ? action.payload.weekStartDay
        : "Monday";
      state.userData.weekStartTime = "00:00:00";
      // TODO: Uncomment once supporting stories are integrated for offset and remove above
      // state.userData.weekStartTime = action.payload.schedulerOffsetEnabled ?
      //   `${action.payload.weekStartTime?.split(":")?.[0] ?? "00"}:00:00`
      //   : "00:00:00"
      // ;
      state.userData.company = action.payload.company;
      state.userData.firstName = action.payload.firstName;
      state.userData.lastName = action.payload.lastName;
      state.userData.username = action.payload.username;
      state.userData.companyId = action.payload.companyId;
      state.userData.attendanceManagement = action.payload["attendMgmt"];
      state.userData.leaveCalendarHeatmap = action.payload["leaveCalendarHeatmap"]
      state.userData.callOffLineEnabledLocations =
        action.payload["callOffLineEnabledLocations"] ?? [];
      state.userData.attendanceManagementEnabledLocations =
        action.payload.attendanceManagementEnabledLocations;
      state.userData.jobSkillLevelMapping = action.payload.jobSkillLevelMapping;
      state.userData.workerSubTypeEnabled = action.payload.workerSubTypeEnabled;
      state.userData.skillLevelRequirementsEnabled =
        action.payload.skillLevelRequirementsEnabled;
      state.userData.defaultShiftSkillLevel = Number(
        action.payload.shiftBySkillsMinSkillLevel
      );

      state.userData.shiftBySkills = action.payload.shiftBySkillsEnabled;
      state.userData.assignShiftToWorkerEnabled =
        action.payload.assignShiftToWorkerEnabled;
      state.userData.allowPartialSignups = action.payload.allowPartialSignups;
      state.userData.shiftWorkerTypeFilterEnabled =
        action.payload.shiftWorkerTypeFilterEnabled ?? false;
      state.userData.badgeNumberEnabled = action.payload.badgeNumberEnabled;
      state.userData.badgeNumberEnabledLocations =
        action.payload.badgeNumberEnabledLocations;
      state.userData.maxBroadcastMessageLength =
        action.payload.maxBroadcastMessageLength ?? 160;
      state.userData.skillsMatrixExportEnabled =
        action.payload.skillsMatrixExportEnabled;
      state.userData.EnableSkillEvaluationForm =
        action.payload.EnableSkillEvaluationForm ?? false;
      state.userData.leaveBalanceUpdateEnabled = action.payload.leaveBalanceUpdateEnabled;
      state.userData.vakaySSOLinkEnabled = action.payload.vakaySSOLinkEnabled;
      state.userData.punchImportEnabled = action.payload.punchImportEnabled;
      state.userData.urgentShiftEnabledLocations = action.payload.urgentShiftEnabledLocations;
      state.userData.minWeekHoursNotifEnabledLocations = action.payload.minWeekHoursNotifEnabledLocations;
      state.userData.supervisorMultilingualEnabled =
        action.payload.supervisorMultilingualEnabled === true;
      state.userData.languagePreference = action.payload.languagePreference ?? null;

      try {
        // HEAP IDENTIFIER USER
        if (
          (process.env.REACT_APP_ENV === "demo" ||
            process.env.REACT_APP_ENV === "prod") &&
          action.payload &&
          (window as any).heap &&
          typeof (window as any).heap.identify === "function"
        ) {
          (window as any).heap.identify(action.payload.id);

          (window as any).heap.addUserProperties({
            firstName: action.payload.firstName,
            lastName: action.payload.lastName,
            userType: isInternelEmail(action.payload.username)
              ? "internal"
              : "external",
            company: action.payload.company,
            companyId: action.payload.companyId,
            userRole: Array.isArray(action.payload.userRoles)
              ? action.payload.userRoles.length > 0
                ? action.payload.userRoles[0]
                : "ShiftSupervisor"
              : "ShiftSupervisor",
            isGATAdmin: action.payload.userRoles.find(
              (role: string) => role === "GATAdmin"
            )
              ? "Yes"
              : "No",
          });
        }
      } catch (e) {
        // Do nothing
      }
    },
    logoutUser: (state) => {
      state.userData.id = "";
      state.userData.userRoles = [];
      state.userData.userRolesWithPermissions = [];
      state.customLogo = [];
      state.authenticated = false;
      state.login.status = "idle";
      state.login.errorMessage = "";
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("permissions");
      localStorage.removeItem("appliedFilters-flex-scheduler");
      localStorage.removeItem("appliedFilters-monthly-activity-report");
      localStorage.removeItem("appliedFilters-daily-activity-report");
      localStorage.removeItem("appliedFilters-monthly-report");
      localStorage.removeItem("appliedFilters-daily-report");
    },
    resetForgotPassword: (state) => {
      state.forgotPassword.status = "idle";
      state.forgotPassword.errorMessage = "";
    },
    resetValidateResetToken: (state) => {
      state.validateResetToken.status = "idle";
      state.validateResetToken.errorMessage = "";
      state.validateResetToken.isValid = undefined;
    },
    resetResetPassword: (state) => {
      state.resetPassword.status = "idle";
      state.resetPassword.errorMessage = "";
    },
    resetLoginErrors: (state) => {
      state.login.status = "idle";
      state.login.errorMessage = "";
      state.login.maxFailedAttempts = 5;
      state.login.failedAttempt = 0;
      state.login.isLocked = false;
      state.verifySSO.status = "idle";
      state.verifySSO.errorMessage = "";
      state.verifySSO.isSSOEnabled = undefined;
      state.verifySSO.ssoLoginUrl = undefined;
      state.authenticateSSO.status = "idle";
      state.authenticateSSO.errorMessage = "";
    },
    setSupervisorRateLimitError: (state) => {
      state.supervisorRateLimitError.isErrorOccurred = true;
    },
    resetSupervisorRateLimitError: (state) => {
      state.supervisorRateLimitError.isErrorOccurred = false;
    },
    resetAssertVakaySSO: (state) => {
      state.assertVakaySSO.status = "idle";
      state.assertVakaySSO.errorMessage = "";
      state.assertVakaySSO.redirectUrl = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.fulfilled, (state, action: any) => {
      const {
        username,
        userRoles,
        id,
        firstName,
        lastName,
        settings,
        shiftSettings,
        company,
        companyId,
        permissions: apiPermissions = [],
      } = action.payload;
      state.userData.username = username;
      state.userData.supervisorMultilingualEnabled =
        settings?.supervisorMultilingualEnabled === true;
      state.userData.languagePreference = action.payload.languagePreference ?? null;
      state.userData.userRoles = userRoles;
      state.userData.permissions = apiPermissions;
      state.userData.userRolesWithPermissions = userRoles.map(
        (role: string) => {
          let returnValue: RoleContextType = {
            role: "ReadOnly",
            permissions: {
              ...ReadOnly,
            },
          };
          switch (role) {
            case "ShiftSupervisor":
              returnValue = {
                role: role,
                permissions: {
                  ...ShiftSupervisor,
                },
              };
              break;
            case "DepartmentManager":
              returnValue = {
                role: role,
                permissions: {
                  ...DepartmentManager,
                },
              };
              break;
            case "HR Manager":
              returnValue = {
                role: role,
                permissions: {
                  ...HRManager,
                  REPORTS: {
                    ...HRManager.REPORTS,
                    UPDATE_LEAVE_BALANCE:
                      settings["leaveBalanceMgmt.enabled"] ?? false,
                  },
                },
              };
              break;
            case "LimitedAccessSupervisor":
              returnValue = {
                role: role,
                permissions: {
                  ...LimitedAccessSupervisor,
                },
              };
              break;
            case "GATAdmin":
              returnValue = {
                role: role,
                permissions: {
                  ...GATAdmin,
                  REPORTS: {
                    ...GATAdmin.REPORTS,
                    UPDATE_LEAVE_BALANCE:
                      settings["leaveBalanceMgmt.enabled"] ?? false,
                  },
                },
              };
              break;
          }
          return returnValue;
        }
      );
      state.userData.id = id;
      state.userData.firstName = firstName;
      state.userData.lastName = lastName;
      state.userData.company = company;
      state.userData.companyId = companyId;
      state.userData.showWorkerProfile = settings.showWorkerProfile;
      state.userData.showHeatMap = settings.showHeatMap;
      state.userData.showJobExpiry = settings.showJobExpiry;
      state.userData.showCrossSkilling = settings.showCrossSkilling;
      state.userData.shiftCancelReasonEnabled =
        settings.shiftCancelReasonEnabled;
      state.userData.showShiftPurpose = settings.showShiftPurpose;

      state.userData.skillPriorityEnabled = settings["skillPriority.Enabled"];
      state.userData.useObsoleteSkills =
        settings["skillPriority.workerScore.useObsoleteSkill"];

      state.userData.customizedBrandingEnabled =
        settings.customizedBrandingEnabled;
      if (!settings.customizedBrandingEnabled) {
        // No extra step require for authentication
        state.authenticated = true;
        state.login.status = "fulfilled";
      }
      state.customLogo = [];
      state.userData.skillExpiryEnabled = settings.skillExpiryEnabled;
      state.userData.certificationsEnabled = settings.certificationsEnabled;
      state.userData.skillInsightsEnabled = settings.skillInsightsEnabled;
      state.userData.jobsMatrixEnabled = settings["jobsMatrix.enabled"];
      state.userData.supervisorDashboardEnabled =
        settings.supervisorDashboardEnabled === true;
      state.userData.fullTimeSchedule = settings.fullTimeSchedule;
      state.userData.ftSchedulerEnabled =
        settings["fullTimeScheduling.enabled"];
      state.userData.ftSchedulerEnabledLocations =
        settings["FullTimeSchedulingEnabledLocations"];
      state.userData.FTScheduleGenerationEnabledLocations = 
    settings["FTScheduleGenerationEnabledLocations"] || [];
      state.userData.showShiftSwap = settings.showShiftSwap;
      state.userData.restrictFlexHours = settings.restrictFlexHours;
      state.userData.isSchedulerOffset = settings.schedulerOffsetEnabled;

      state.userData.attendanceManagement = settings["attendMgmt.enabled"];
      state.userData.leaveCalendarHeatmap =
        settings["leaveCalendarHeatmap.enabled"];
      state.userData.callOffLineEnabledLocations =
        settings["CallOffLineEnabledLocations"] ?? [];
      state.userData.attendanceManagementEnabledLocations =
        settings["AttendanceMgmtEnabledLocations"];
      state.userData.jobSkillLevelMapping =
        settings["jobSkill.skillLevelEnabled"];
      state.userData.workerSubTypeEnabled = settings["workerSubType.enabled"];
      state.userData.skillLevelRequirementsEnabled =
        settings["skillLevelRequirementsEnabled"];

      state.userData.shiftBySkills = settings.shiftBySkillsEnabled;
      state.userData.badgeNumberEnabled = settings["badgeNumber.Enabled"];
      state.userData.badgeNumberEnabledLocations =
        settings["BadgeNumberEnabledLocations"];

      state.userData.defaultDurationHrs = shiftSettings.defaultDurationHrs || 2;
      state.userData.minDurationHrs = shiftSettings.minDurationHrs || 1;
      state.userData.maxDurationHrs = shiftSettings.maxDurationHrs || 8;
      state.userData.weekStartDay = settings.schedulerOffsetEnabled
        ? shiftSettings.weekStartDay
        : "Monday";
      state.userData.weekStartTime = "00:00:00";
      // TODO: Uncomment once supporting stories are integrated for offset and remove above
      // state.userData.weekStartTime = settings.schedulerOffsetEnabled ?
      //   `${shiftSettings.weekStartTime?.split(":")?.[0] ?? "00"}:00:00`
      //   : "00:00:00"
      // ;

      state.userData.defaultShiftSkillLevel = Number(
        settings.shiftBySkillsMinSkillLevel
      );
      state.userData.assignShiftToWorkerEnabled =
        settings.assignShiftToWorkerEnabled;
      state.userData.allowPartialSignups =
        settings["partialShiftSignup.enabled"];
      state.userData.shiftWorkerTypeFilterEnabled =
        settings["shift.workerTypeFilter.enabled"] ?? false;
      state.userData.maxBroadcastMessageLength =
        settings.maxBroadcastMessageLength ?? 160;
      state.userData.skillsMatrixExportEnabled =
        settings["skillMatrix.excelExport.enabled"] ?? false;
      state.userData.EnableSkillEvaluationForm =
        settings.EnableSkillEvaluationForm ?? false;
      state.userData.leaveBalanceUpdateEnabled = settings["leaveBalanceMgmt.enabled"] ?? false;
      state.userData.punchImportEnabled = settings["punchImport.enabled"] ?? false;
      state.userData.vakaySSOLinkEnabled = settings["EnableVakaySSOLink"] ?? false;
      state.userData.urgentShiftEnabledLocations =
        settings["UrgentShiftEnabledLocations"] || [];
      state.userData.minWeekHoursNotifEnabledLocations =
        Array.isArray(settings["MinWeekHoursNotifEnabledLocations"]) ? settings["MinWeekHoursNotifEnabledLocations"] : [];
    });
    builder.addCase(loginUser.pending, (state) => {
      state.login.status = "pending";
    });
    builder.addCase(loginUser.rejected, (state, action: any) => {
      if (action?.payload?.status === 429) {
        state.supervisorRateLimitError.isErrorOccurred = true;
        state.login.status = "error";
      } else {
        state.login.status = "error";
        state.login.errorMessage =
          action.payload?.message ?? i18next.t("common_error-try-again");
        state.login.maxFailedAttempts = action.payload?.maxFailedAttempts ?? 0;
        state.login.failedAttempt = action.payload?.failedAttempt ?? 0;
        state.login.isLocked = action.payload?.isLocked ?? false;
      }
    });
    builder.addCase(verifySSO.fulfilled, (state, action: any) => {
      state.verifySSO.status = "fulfilled";
      state.verifySSO.isSSOEnabled =
        action.payload.isSSOEnabled === null ||
        action.payload.isSSOEnabled === ""
          ? undefined
          : action.payload.isSSOEnabled;
      state.verifySSO.ssoLoginUrl =
        action.payload.ssoLoginUrl === null || action.payload.ssoLoginUrl === ""
          ? undefined
          : action.payload.ssoLoginUrl;
    });
    builder.addCase(verifySSO.pending, (state) => {
      state.verifySSO.status = "pending";
      state.login.status = "idle";
      state.login.errorMessage = "";
      state.verifySSO.errorMessage = "";
      state.authenticateSSO.status = "idle";
      state.authenticateSSO.errorMessage = "";
    });
    builder.addCase(verifySSO.rejected, (state, action: any) => {
      if (action?.payload?.status === 429) {
        state.supervisorRateLimitError.isErrorOccurred = true;
        state.verifySSO.status = "error";
      } else {
        state.verifySSO.status = "error";
        state.verifySSO.errorMessage =
          action.payload?.message ?? i18next.t("common_error-try-again");
        state.login.maxFailedAttempts = action.payload?.maxFailedAttempts ?? 0;
        state.login.failedAttempt = action.payload?.failedAttempt ?? 0;
        state.login.isLocked = action.payload?.isLocked ?? false;
      }
    });
    builder.addCase(authenticateSSO.fulfilled, (state, action: any) => {
      const {
        username,
        userRoles,
        id,
        firstName,
        lastName,
        settings,
        shiftSettings,
        company,
        companyId,
        permissions: apiPermissions = [],
        //defaultUserView,
      } = action.payload;
      state.userData.username = username;
      state.userData.supervisorMultilingualEnabled =
        settings?.supervisorMultilingualEnabled === true;
      state.userData.languagePreference = action.payload.languagePreference ?? null;
      state.userData.userRoles = userRoles;
      state.userData.permissions = apiPermissions;
      state.userData.userRolesWithPermissions = userRoles.map(
        (role: string) => {
          let returnValue: RoleContextType = {
            role: "ReadOnly",
            permissions: {
              ...ReadOnly,
            },
          };
          switch (role) {
            case "ShiftSupervisor":
              returnValue = {
                role: role,
                permissions: {
                  ...ShiftSupervisor,
                },
              };
              break;
            case "DepartmentManager":
              returnValue = {
                role: role,
                permissions: {
                  ...DepartmentManager,
                },
              };
              break;
            case "HR Manager":
              returnValue = {
                role: role,
                permissions: {
                  ...HRManager,
                  REPORTS: {
                    ...HRManager.REPORTS,
                    UPDATE_LEAVE_BALANCE:
                      settings["leaveBalanceMgmt.enabled"] ?? false,
                  },
                },
              };
              break;
            case "LimitedAccessSupervisor":
              returnValue = {
                role: role,
                permissions: {
                  ...LimitedAccessSupervisor,
                },
              };
              break;
            case "GATAdmin":
              returnValue = {
                role: role,
                permissions: {
                  ...GATAdmin,
                  REPORTS: {
                    ...GATAdmin.REPORTS,
                    UPDATE_LEAVE_BALANCE:
                      settings["leaveBalanceMgmt.enabled"] ?? false,
                  },
                },
              };
              break;
          }
          return returnValue;
        }
      );
      state.userData.id = id;
      state.userData.firstName = firstName;
      state.userData.lastName = lastName;
      state.userData.company = company;
      state.userData.companyId = companyId;
      state.userData.showWorkerProfile = settings.showWorkerProfile;
      state.userData.showHeatMap = settings.showHeatMap;
      state.userData.showJobExpiry = settings.showJobExpiry;
      state.userData.showCrossSkilling = settings.showCrossSkilling;
      state.userData.shiftCancelReasonEnabled =
        settings.shiftCancelReasonEnabled;
      state.userData.showShiftPurpose = settings.showShiftPurpose;
      state.userData.allowPartialSignups =
        settings["partialShiftSignup.enabled"];
      state.userData.shiftWorkerTypeFilterEnabled =
        settings["shift.workerTypeFilter.enabled"] ?? false;

      state.userData.skillPriorityEnabled = settings["skillPriority.Enabled"];
      state.userData.useObsoleteSkills =
        settings["skillPriority.workerScore.useObsoleteSkill"];

      state.userData.customizedBrandingEnabled =
        settings.customizedBrandingEnabled;
      if (!settings.customizedBrandingEnabled) {
        // No extra step require for authentication
        state.authenticated = true;
        state.login.status = "fulfilled";
      }
      state.userData.skillExpiryEnabled = settings.skillExpiryEnabled;
      state.userData.certificationsEnabled = settings.certificationsEnabled;
      state.userData.skillInsightsEnabled = settings.skillInsightsEnabled;
      state.userData.jobsMatrixEnabled = settings["jobsMatrix.enabled"];
      state.userData.supervisorDashboardEnabled =
        settings.supervisorDashboardEnabled === true;
      state.userData.defaultUserView = settings.defaultUserView;
      state.userData.fullTimeSchedule = settings.fullTimeSchedule;
      state.userData.ftSchedulerEnabled =
        settings["fullTimeScheduling.enabled"];
      state.userData.ftSchedulerEnabledLocations =
        settings["FullTimeSchedulingEnabledLocations"];
      state.userData.FTScheduleGenerationEnabledLocations = 
    settings["FTScheduleGenerationEnabledLocations"] || [];
      state.userData.showShiftSwap = settings.showShiftSwap;
      state.userData.restrictFlexHours = settings.restrictFlexHours;
      state.userData.isSchedulerOffset = settings.schedulerOffsetEnabled;
      state.userData.attendanceManagement = settings["attendMgmt.enabled"];
      state.userData.leaveCalendarHeatmap =
        settings["leaveCalendarHeatmap.enabled"];
      state.userData.callOffLineEnabledLocations =
        settings["CallOffLineEnabledLocations"] ?? [];
      state.userData.attendanceManagementEnabledLocations =
        settings["AttendanceMgmtEnabledLocations"];
      state.userData.jobSkillLevelMapping =
        settings["jobSkill.skillLevelEnabled"];
      state.userData.workerSubTypeEnabled = settings["workerSubType.enabled"];
      state.userData.skillLevelRequirementsEnabled =
        settings["skillLevelRequirementsEnabled"];
      state.userData.defaultShiftSkillLevel = Number(
        settings.shiftBySkillsMinSkillLevel
      );
      state.userData.shiftBySkills = settings.shiftBySkillsEnabled;
      state.userData.badgeNumberEnabled = settings["badgeNumber.Enabled"];
      state.userData.badgeNumberEnabledLocations =
        settings["BadgeNumberEnabledLocations"];

      state.userData.defaultDurationHrs = shiftSettings.defaultDurationHrs ?? 2;
      state.userData.minDurationHrs = shiftSettings.minDurationHrs ?? 1;
      state.userData.maxDurationHrs = shiftSettings.maxDurationHrs ?? 8;
      state.userData.weekStartDay = settings.schedulerOffsetEnabled
        ? shiftSettings.weekStartDay
        : "Monday";
      state.userData.weekStartTime = "00:00:00";
      // TODO: Uncomment once supporting stories are integrated for offset and remove above
      // state.userData.weekStartTime = settings.schedulerOffsetEnabled ?
      //   `${shiftSettings.weekStartTime?.split(":")?.[0] ?? "00"}:00:00`
      //   : "00:00:00"
      // ;
      state.userData.assignShiftToWorkerEnabled =
        settings.assignShiftToWorkerEnabled;
      state.userData.maxBroadcastMessageLength =
        settings.maxBroadcastMessageLength ?? 160;
      state.authenticateSSO.status = "fulfilled";
      state.userData.skillsMatrixExportEnabled =
        settings["skillMatrix.excelExport.enabled"] ?? false;
      state.userData.EnableSkillEvaluationForm =
        settings.EnableSkillEvaluationForm ?? false;
      state.userData.leaveBalanceUpdateEnabled =
        settings["leaveBalanceMgmt.enabled"] ?? false;
      state.userData.punchImportEnabled =
        settings["punchImport.enabled"] ?? false;
      state.userData.vakaySSOLinkEnabled =
        settings["EnableVakaySSOLink"] ?? false;
      state.userData.urgentShiftEnabledLocations =
        settings["UrgentShiftEnabledLocations"] || [];
      state.userData.minWeekHoursNotifEnabledLocations =
        Array.isArray(settings["MinWeekHoursNotifEnabledLocations"]) ? settings["MinWeekHoursNotifEnabledLocations"] : [];

      try {
        // HEAP IDENTIFIER USER
        if (
          (process.env.REACT_APP_ENV === "demo" ||
            process.env.REACT_APP_ENV === "prod") &&
          action.payload &&
          (window as any).heap &&
          typeof (window as any).heap.identify === "function"
        ) {
          (window as any).heap.identify(id);

          (window as any).heap.addUserProperties({
            firstName: firstName,
            lastName: lastName,
            userType: isInternelEmail(username) ? "internal" : "external",
            company: company,
            companyId: companyId,
            userRole: userRoles
              ? userRoles.length > 0
                ? userRoles[0] ?? "ShiftSupervisor"
                : "ShiftSupervisor"
              : "ShiftSupervisor",
            isGATAdmin: userRoles.find((role: string) => role === "GATAdmin")
              ? "Yes"
              : "No",
          });
        }
      } catch (e) {
        // Do nothing
      }
    });
    builder.addCase(authenticateSSO.pending, (state) => {
      state.authenticateSSO.status = "pending";
      state.login.status = "idle";
      state.login.errorMessage = "";
      state.verifySSO.status = "idle";
      state.verifySSO.errorMessage = "";
    });
    builder.addCase(authenticateSSO.rejected, (state, action: any) => {
      if (action?.payload?.status === 429) {
        state.supervisorRateLimitError.isErrorOccurred = true;
        state.authenticateSSO.status = "error";
      } else {
        state.authenticateSSO.status = "error";
        state.authenticateSSO.errorMessage =
          action.payload?.message ?? i18next.t("common_error-try-again");
      }
    });
    builder.addCase(forgotPassword.fulfilled, (state) => {
      state.forgotPassword.status = "fulfilled";
    });
    builder.addCase(forgotPassword.pending, (state) => {
      state.forgotPassword.status = "pending";
    });
    builder.addCase(forgotPassword.rejected, (state, action: any) => {
      if (action?.payload?.status === 429) {
        state.supervisorRateLimitError.isErrorOccurred = true;
        state.forgotPassword.status = "error";
      } else {
        state.forgotPassword.status = "error";
        state.forgotPassword.errorMessage =
          action.payload ?? i18next.t("common_error-try-again");
      }
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.resetPassword.status = "fulfilled";
    });
    builder.addCase(resetPassword.pending, (state) => {
      state.resetPassword.status = "pending";
    });
    builder.addCase(resetPassword.rejected, (state, action: any) => {
      if (action?.payload?.status === 429) {
        state.supervisorRateLimitError.isErrorOccurred = true;
        state.resetPassword.status = "error";
      } else {
        state.resetPassword.status = "error";
        // console.log(action.payload);
        state.resetPassword.errorMessage =
          action.payload?.message ?? i18next.t("common_error-try-again");
      }
    });
    builder.addCase(getCustomLogo.fulfilled, (state, action: any) => {
      state.getCustomLogo.status = "fulfilled";
      state.login.status = "fulfilled";
      state.customLogo = action.payload ?? [];
    });
    builder.addCase(getCustomLogo.pending, (state) => {
      state.getCustomLogo.status = "pending";
    });
    builder.addCase(getCustomLogo.rejected, (state, action: any) => {
      if (action?.payload?.status === 429) {
        state.supervisorRateLimitError.isErrorOccurred = true;
        state.getCustomLogo.status = "error";
      } else {
        state.getCustomLogo.status = "error";
        state.customLogo = [];
        // console.log(action.payload);
        state.getCustomLogo.errorMessage =
          action.payload?.message ?? i18next.t("common_error-try-again");
      }
    });
    builder.addCase(validateResetToken.fulfilled, (state, action: any) => {
      state.validateResetToken.isValid = true;
      state.validateResetToken.status = "fulfilled";
      state.validateResetToken.errorMessage = "";
    });
    builder.addCase(validateResetToken.pending, (state: any, action: any) => {
      state.validateResetToken.status = "pending";
      state.validateResetToken.errorMessage = "";
    });
    builder.addCase(validateResetToken.rejected, (state: any, action: any) => {
      state.validateResetToken.isValid = false;
      state.validateResetToken.status = "error";
      state.validateResetToken.errorMessage =
        action.payload?.message ?? i18next.t("common_error-try-again");
    });
    builder.addCase(assertVakaySSO.pending, (state) => {
      state.assertVakaySSO.status = "pending";
    });
    builder.addCase(assertVakaySSO.fulfilled, (state, action: any) => {
      state.assertVakaySSO.status = "fulfilled";
      state.assertVakaySSO.redirectUrl = action.payload.redirectUrl;
    });
    builder.addCase(assertVakaySSO.rejected, (state, action: any) => {
      state.assertVakaySSO.status = "error";
      state.assertVakaySSO.errorMessage = action.payload?.message ?? i18next.t("common_error-occurred");
    });
  },
});

export const userSelector = (state: { user: UserState }) => state.user;

export const {
  setUserId,
  setUser,
  setAuthenticated,
  logoutUser,
  resetForgotPassword,
  resetResetPassword,
  resetValidateResetToken,
  resetLoginErrors,
  setSupervisorRateLimitError,
  resetSupervisorRateLimitError,
  resetAssertVakaySSO,
} = userSlice.actions;

export default userSlice.reducer;
